<template>
  <div class="container">
    <div class="clearfix">
      <div class="col12">
        <div style="font-weight: 600; padding: 0; margin: 0;">AUCTION SETUP</div>
        <h2 v-if="editMode">Edit Existing Auction</h2>
        <h2 v-else>Create New Auction</h2>
      </div>
      <div class="col12" style="text-align: right; margin-top: 1em;">
        <v-icon>exit_to_app</v-icon> <router-link to="/dashboard">Exit</router-link>
      </div>
    </div>

    <div class="create-listing-progress-menu">
      <CreateListingProgressMenu :steps="steps" />
    </div>

    <div v-if="editingDisabled">
      <v-alert outline color="red darken-2" icon="block" :value="true" transition="scale-transition">
        This auction cannot be edited anymore since the status is "{{ auctionStatusLabel(auctionStatus) }}".
      </v-alert>
    </div>

    <div v-else-if="currentUserRole === 'ASSISTANT'">
      <v-alert outline color="red darken-2" icon="block" :value="true" transition="scale-transition">
        Dear user, your tenant role doesn't have permission to create a new auction. Please send a mesage to tenant admin if you need access to create auctions.
      </v-alert>
    </div>

    <div class="create-listing-content" v-else-if="!loading">
      <component :is="childComponent" ref="childComponent"/>

      <fieldset class="clearfix">
        <div class="col12">
          <button class="btn-neutral" @click.prevent="prevStep"><v-icon dark>chevron_left</v-icon> {{ prevStepLabel }}</button>
        </div>
        <div class="col12" style="text-align: right">
          <button @click.prevent="nextStep">{{ nextStepLabel }} <v-icon dark>chevron_right</v-icon> </button>
        </div>
      </fieldset>
    </div>

    <div v-else>
      Loading...
    </div>

  </div>
</template>

<script>
  import { auctionStatuses, auctionStatusLabel, auctionRoles } from '../utils/PfxUtils.js'
  import CreateListingProgressMenu from '../components/CreateListingProgressMenu.vue'
  import CreateListingProperty from '../components/CreateListingProperty.vue'
  import CreateListingAuction from '../components/CreateListingAuction.vue'
  import CreateListingBuyers from '../components/CreateListingBuyers.vue'
  // import CreateListingDelegate from '../components/CreateListingDelegate.vue'
  import CreateListingSummary from '../components/CreateListingSummary.vue'
  import CreateListingPreview from '../components/CreateListingPreview.vue'

  export default {
    name: 'CreateListingPage',
    components: {
      CreateListingProgressMenu,
      CreateListingProperty,
      CreateListingAuction,
      CreateListingBuyers,
      // CreateListingDelegate,
      CreateListingSummary,
      CreateListingPreview
    },
    data() {
      return {
        auctionStatuses,
        loading: true,
        editingDisabled: false,
        currentUserRole: '',
        stepsCreate: [
          { label: 'Property Data', component: CreateListingProperty },
          { label: 'Auction Data', component: CreateListingAuction },
          { label: 'Buyers Requirements', component: CreateListingBuyers },
          { label: 'Summary', component: CreateListingSummary },
          { label: 'Preview', component: CreateListingPreview }
        ],
        stepsEdit: [
          // { label: 'Delegate Agents', component: CreateListingDelegate },
          { label: 'Property Data', component: CreateListingProperty },
          { label: 'Auction Data', component: CreateListingAuction },
          { label: 'Buyers Requirements', component: CreateListingBuyers },
          { label: 'Summary', component: CreateListingSummary },
          { label: 'Preview', component: CreateListingPreview }
        ],
        steps: []
      }
    },
    computed: {
      editMode() {
        return this.$store.state.newAuctionStorage.edit === true
      },
      currentStep() {
        return this.$store.state.newAuctionStorage.currentStep || 0
      },
      prevStepLabel() {
        return this.currentStep <= 0 ? 'Cancel' : 'Back'
      },
      nextStepLabel() {
        if (this.currentStep + 1 >= this.steps.length) {
          return 'Finish and save information'
        }
        return 'Next: ' + this.steps[this.currentStep + 1].label
      },
      childComponent() {
        return this.steps[this.currentStep].component
      },
      auctionStatus() {
        return this.$store.state.newAuctionStorage.auction.status
      }
    },

    events: {
      refresh : function(){
        this.getData()
      }
    },

    created() {
     this.getData()
    },

    methods: {
      auctionStatusLabel,

      getData(){
        this.loading = true
        this.$store.commit('setCreateListingProgressCurrentStep', 0)
        const auctionId = this.$route.params.auctionId || 0
        if (auctionId) {
          this.steps = this.stepsEdit
          this.$store.commit('setDataToNewAuctionStorage', { edit: true })
          this.loading = true
          this.$store.state.loadingSpinner = true
          this.$PfxBackendApi.getAuction(auctionId).then(res => {
            console.log('getAuction res', res)
            const auction = res.data || {}
            if (auction.status !== auctionStatuses.DRAFT.value && auction.status !== auctionStatuses.OPEN.value
                  && auction.status !== auctionStatuses.FREEZE.value) {
              this.editingDisabled = true
            }
            const buyerRequirements = auction.buyerRequirements.map(req => req.docType) // convert auction.buyerRequirements into internal representation => simple string arrray
            this.$store.commit('setDataToNewAuctionStorage', { auction, auctionMetadata: auction.metadata, buyerRequirements })

            // get invited but pending seller (if any)
            this.$PfxBackendApi.getAuctionInvitations(auction.tenant.subdomain, auction.id)
              .then(res => {
                console.debug('getAuctionInvitations res', res)
                const invitations = res.data || []
                const sellerRoles = invitations.filter(i => i.auctionInvitationRole === 'SELLER')
                if (sellerRoles.length > 0) {
                  auction.sellerEmail = sellerRoles[0].email
                  this.$store.commit('setDataToNewAuctionStorage', { auction })
                  console.log('invitedSellerEmail [%s]', auction.sellerEmail)
                }
              })
              .catch(err => console.error('getAuctionInvitations err', err))

            // load participants > resolve accepted seller (if any)
            this.$PfxBackendApi.getAuctionParticipants(auction.tenant.subdomain, auction.id)
              .then(res => {
                console.log('getAuctionParticipants res', res)
                const roles = res.data || []
                const sellerRoles = roles.filter(ar => ar.role === auctionRoles.SELLER.value)
                if (sellerRoles.length > 0) {
                  this.$PfxBackendApi.getUser(sellerRoles[0].userId)
                    .then(res => {
                      console.log('getUserProfile (seller)', res)
                      this.$store.commit('setDataToNewAuctionStorage', { sellerProfile: res.data || {} })
                    })
                    .catch(err => console.error('getUserProfile (seller) err', err))
                }
              })
              .catch(err => console.error('getAuctionParticipants err', err))

          }).catch(err => {
            console.error('getAuction err', err)
            this.$notify({ type: 'danger', message: 'Cannot fetch data about auction with id: ' + auctionId })
          }).then(() => {
            this.$store.state.loadingSpinner = false
            this.loading = false
          })

        } else {
          console.log('USER: ', this.$store.state.user)
          this.currentUserRole = this.$store.state.user.role
          this.steps = this.stepsCreate
          if (this.$store.state.newAuctionStorage.edit === true) { // clicked "create new auction" button but store has data about edit form...
            this.$store.commit('clearNewAuctionStorage') // ...clear store data before opening the wizard form
          }
          this.$store.commit('setDataToNewAuctionStorage', { edit: false })
          this.loading = false
        }
      },
      prevStep() {
        const step = (this.$store.state.newAuctionStorage.currentStep || 0) - 1
        if (step < 0) {
          this.$router.push('/dashboard')
        } else {
          this.$store.commit('setCreateListingProgressCurrentStep', step)
          window.scrollTo(0,0)
        }
      },
      nextStep() {
        if (this.$refs.childComponent.beforeNextStep) {
          const ok = this.$refs.childComponent.beforeNextStep()
          if (!ok) return
        }

        if (this.$refs.childComponent.nextStep) {
          this.$refs.childComponent.nextStep()

        } else {
          const step = (this.$store.state.newAuctionStorage.currentStep || 0) + 1
          this.$store.commit('setCreateListingProgressCurrentStep', step)
        }
        window.scrollTo(0,0)
      }
    }
  }
</script>

<style lang="scss" scoped>

  @import 'node_modules/jeet/scss/index';

  .col12 { @include column(1/2); }

  h2 {
    font-family: 'Encode Sans', sans-serif;
    font-weight: 300;
    font-size: 1.8rem;
    margin: 0 0 .5rem;
  }

</style>



// WEBPACK FOOTER //
// src/components/CreateListingPage.vue